import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dadvq2jep053ijub164g";

type Status = "idle" | "success" | "error";

const quickLinks = [
  { href: "#hot", label: "今日瓜田" },
  { href: "#column", label: "瓜棚栏目" },
  { href: "#rank", label: "瓜摊热榜" },
  { href: "#guide", label: "吃瓜指南" },
];

const columnLinks = [
  { href: "#column", label: "明星动态" },
  { href: "#column", label: "影视综艺" },
  { href: "#column", label: "圈内八卦" },
  { href: "#column", label: "红毯现场" },
];

export default function Footer() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubscribe = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") || "").trim();
    const showGenericSuccess = () => {
      setStatus("success");
      setMessage("订阅成功！最新吃瓜快讯将第一时间发到您的邮箱，欢迎常来瓜田坐坐。");
      form.reset();
    };

    // Anti-spam: silently drop empty honeypot field
    data.delete("website_alt");

    if (honeypot) {
      // Bot signal — present identical success feedback without submitting
      showGenericSuccess();
      return;
    }

    const payload = new URLSearchParams(data as unknown as Record<string, string>);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        showGenericSuccess();
      } else {
        setStatus("error");
        setMessage(typeof serverMsg === "string" && serverMsg ? serverMsg : "订阅未能完成，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络似乎开了小差，订阅未能完成，请稍后重试。");
    }
  };

  return (
    <footer id="about" className="scroll-mt-20 bg-accent-100">
      <div className="mx-auto max-w-[1440px] px-4 pt-10 md:px-8 md:pt-14">
        <div id="subscribe" className="scroll-mt-28">
          <div className="grid grid-cols-1 gap-8 overflow-hidden rounded-3xl bg-primary-500 p-7 text-background-50 md:p-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <h2 className="text-2xl font-bold leading-snug md:text-3xl">
                新瓜上架，怕错过？
                <br />
                把吃瓜快讯放进你的邮箱。
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
                留下常用邮箱，最新热料、热榜更新与独家线索整理会在第一时间送达。我们只发快讯，不做打扰，随时可一键退订。
              </p>
            </div>
            <div className="lg:col-span-5">
              <form
                onSubmit={handleSubscribe}
                data-readdy-form="dadvq2jep053ijub164g"
                className="flex flex-col gap-3 sm:flex-row sm:items-center"
                noValidate
              >
                <div className="relative flex-1">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-lg text-primary-200" aria-hidden="true"></i>
                  <label className="sr-only" htmlFor="sub-email">邮箱地址</label>
                  <input
                    id="sub-email"
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    placeholder="输入你的邮箱地址"
                    className="h-12 w-full rounded-full bg-primary-50/15 pl-11 pr-4 text-sm text-background-50 placeholder:text-primary-200 outline-none transition-colors focus:bg-primary-600/60 focus:ring-2 focus:ring-background-50/60"
                  />
                </div>
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
                >
                  订阅吃瓜
                </button>
                <input
                  type="text"
                  name="website_alt"
                  className="honeypot-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </form>
              {status === "success" && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-accent-500/15 px-4 py-2.5 text-sm text-accent-100">
                  <i className="ri-checkbox-circle-fill text-accent-300" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-secondary-500/20 px-4 py-2.5 text-sm text-secondary-100">
                  <i className="ri-alert-line" aria-hidden="true"></i>
                  {message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 py-10 sm:grid-cols-2 lg:grid-cols-12 md:py-12">
          <div className="lg:col-span-5">
            <a href="#top" className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-2xl bg-primary-500 text-background-50">
                <i className="ri-megaphone-fill text-lg leading-none" aria-hidden="true"></i>
              </span>
              <span className="font-display-head text-xl text-foreground-950">爆料网</span>
            </a>
            <h3 className="sr-only">关于爆料网</h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600">
              爆料网定位为「娱乐圈吃瓜现场一站式平台」，专注娱乐八卦、明星动态与圈内热料的采集、核实与整理。我们的理念是：把每一口瓜的来龙去脉讲清楚，比只图热闹更有价值。坚持来源可溯、实锤与传闻分开标注、有误必纠的内容原则，陪你理性又快乐地吃瓜。
            </p>
            <div className="mt-5 flex items-center gap-4 text-sm text-foreground-600">
              <a
                href="mailto:service@baoliao.net"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-4 py-2 transition-colors hover:text-primary-700"
              >
                <i className="ri-mail-fill text-primary-600" aria-hidden="true"></i>
                service@baoliao.net
              </a>
              <span className="hidden items-center gap-1.5 sm:inline-flex">
                <i className="ri-time-line text-primary-600" aria-hidden="true"></i>
                编辑部 24h 在线滚动更新
              </span>
            </div>
          </div>

          <nav className="lg:col-span-2" aria-label="页脚快速导航">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-foreground-950">快速导航</h4>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-sm text-foreground-600 transition-colors hover:text-primary-700">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="lg:col-span-2" aria-label="页脚栏目导航">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-foreground-950">瓜棚栏目</h4>
            <ul className="mt-4 space-y-2.5">
              {columnLinks.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="text-sm text-foreground-600 transition-colors hover:text-primary-700">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-foreground-950">内容原则</h4>
            <ul className="mt-4 space-y-3">
              {[
                { icon: "ri-focus-3-line", text: "来源可溯，实锤与传闻分明" },
                { icon: "ri-error-warning-line", text: "报道失实，核实后即删即改" },
                { icon: "ri-heart-3-line", text: "理性吃瓜，拒绝无端引导对立" },
              ].map((p) => (
                <li key={p.text} className="flex items-start gap-2 text-sm text-foreground-600">
                  <i className={`${p.icon} mt-0.5 text-accent-700`} aria-hidden="true"></i>
                  {p.text}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-accent-200 py-6 text-xs text-foreground-500 sm:flex-row">
          <p>© {new Date().getFullYear()} 爆料网 · 娱乐圈吃瓜现场平台，保留所有权利。</p>
          <p className="inline-flex items-center gap-1.5">
            <i className="ri-leaf-line text-accent-700" aria-hidden="true"></i>
            理性吃瓜 · 瓜更甜
          </p>
        </div>
      </div>
    </footer>
  );
}